// AdminStudentRequestsManager.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Divider,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import api from '../../../API/axiosInstance';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

ModuleRegistry.registerModules([AllCommunityModule]);
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const AdminStudentRequestsManager = ({ open, onClose }) => {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deptFilter, setDeptFilter] = useState(null);

  const actionCellRenderer = useCallback((params) => {
    if (params.data.status !== 'Pending') {
      return (
        <Chip
          label={params.data.status}
          color={params.data.status === 'Approved' ? 'success' : 'error'}
          size="small"
          sx={{ fontSize: '11px', height: '24px' }}
        />
      );
    }

    return (
      <Box display="flex" gap={0.5} alignItems="center">
        <Tooltip title="View Details">
          <IconButton
            size="small"
            color="info"
            onClick={() => {
              setSelectedRequest(params.data);
              setDetailsDialogOpen(true);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Approve">
          <IconButton
            size="small"
            color="success"
            onClick={() => {
              setSelectedRequest(params.data);
              setApproveDialogOpen(true);
            }}
          >
            <CheckCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reject">
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              setSelectedRequest(params.data);
              setRejectDialogOpen(true);
            }}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 60,
      pinned: "left",
      resizable: false,
      sortable: false,
      filter: false,
      lockPosition: true,
    },
    {
      field: "studentName",
      headerName: "Student Name",
      width: 160,
    },
    {
      field: "erpStudentId",
      headerName: "ERP ID",
      width: 90,
    },
    {
      field: "rollNum",
      headerName: "Roll #",
      width: 90,
    },
    {
      field: "regNum",
      headerName: "Reg #",
      width: 90,
    },
    {
      field: "email",
      headerName: "Email",
      width: 180,
    },
    {
      field: "mobile",
      headerName: "Mobile",
      width: 110,
    },
    {
      field: "whatsAppNumber",
      headerName: "WhatsApp",
      width: 110,
    },
    {
      field: "batchYear",
      headerName: "Batch",
      width: 80,
    },
    {
      field: "requestedDate",
      headerName: "Requested Date",
      width: 130,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-GB');
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      cellRenderer: (params) => {
        const status = params.value;
        const color = status === 'Approved' ? 'success' : status === 'Rejected' ? 'error' : 'warning';
        return (
          <Chip 
            label={status} 
            color={color} 
            size="small"
            sx={{ fontSize: '11px', height: '24px' }}
          />
        );
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 140,
      pinned: "right",
      cellRenderer: actionCellRenderer,
      sortable: false,
      filter: false,
      resizable: false,
    },
  ], [actionCellRenderer]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: false,
  }), []);

  const onGridReady = useCallback((params) => {
    gridRef.current = params.api;
  }, []);

  const fetchRequests = useCallback(async (status = 'Pending', deptId = null) => {
    try {
      setLoading(true);
      const params = {};
      if (status) params.status = status;
      if (deptId) params.deptId = deptId;
      
      const response = await api.get('/StudentRequest/pending-requests', { params });
      
      const data = Array.isArray(response.data) ? response.data : [];
      // Convert PascalCase to camelCase
      const transformedData = data.map(request => {
        const convertKeys = (obj) => {
          const converted = {};
          for (const key in obj) {
            const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
            converted[camelKey] = obj[key];
          }
          return converted;
        };
        return convertKeys(request);
      }).map(request => ({
        requestId: request.requestId,
        erpStudentId: request.erpStudentId,
        uploadId: request.uploadId,
        studentName: request.studentName || '',
        email: request.email || '',
        mobile: request.mobile || '',
        whatsAppNumber: request.whatsAppNumber || '',
        rollNum: request.rollNum || '',
        regNum: request.regNum || '',
        batchYear: request.batchYear || '',
        requestedDate: request.requestedDate,
        status: request.status || 'Pending',
        isConnected: request.isConnected || false,
        remarks: request.remarks || '',
        rejectionReason: request.rejectionReason || '',
        approvedDate: request.approvedDate,
        rejectedDate: request.rejectedDate,
        connectedDate: request.connectedDate,
        deptId: request.deptId,
        courseId: request.courseId,
      }));
      
      setRowData(transformedData);
    } catch (error) {
      console.error("Error fetching requests:", error);
      setRowData([]);
      setSnackbar({
        open: true,
        message: 'Failed to fetch requests. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchRequests(statusFilter, deptFilter);
    }
  }, [open, statusFilter, deptFilter, fetchRequests]);

  const handleApprove = useCallback(async () => {
    if (!selectedRequest || !selectedRequest.requestId) {
      setSnackbar({
        open: true,
        message: 'Invalid request. Please try again.',
        severity: 'error'
      });
      return;
    }

    setProcessing(true);
    try {
      const payload = {
        RequestId: selectedRequest.requestId,
        ApprovedBy: 1, // TODO: Get from auth context
        Remarks: remarks.trim() || null
      };
      
      console.log('Approve request payload:', payload);
      
      const response = await api.post('/StudentRequest/approve-request', payload);

      if (response.data) {
        setSnackbar({
          open: true,
          message: response.data.message || 'Request approved successfully! Student has been connected to the portal.',
          severity: 'success'
        });
        setApproveDialogOpen(false);
        setSelectedRequest(null);
        setRemarks('');
        fetchRequests(statusFilter, deptFilter);
      }
    } catch (error) {
      console.error('Error approving request:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to approve request. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setProcessing(false);
    }
  }, [selectedRequest, remarks, statusFilter, deptFilter, fetchRequests]);

  const handleReject = useCallback(async () => {
    if (!selectedRequest || !selectedRequest.requestId) {
      setSnackbar({
        open: true,
        message: 'Invalid request. Please try again.',
        severity: 'error'
      });
      return;
    }
    
    if (!rejectionReason.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a rejection reason.',
        severity: 'warning'
      });
      return;
    }

    setProcessing(true);
    try {
      const payload = {
        RequestId: selectedRequest.requestId,
        RejectedBy: 1, // TODO: Get from auth context
        RejectionReason: rejectionReason.trim()
      };
      
      console.log('Reject request payload:', payload);
      
      const response = await api.post('/StudentRequest/reject-request', payload);

      if (response.data) {
        setSnackbar({
          open: true,
          message: response.data.message || 'Request rejected successfully.',
          severity: 'success'
        });
        setRejectDialogOpen(false);
        setSelectedRequest(null);
        setRejectionReason('');
        fetchRequests(statusFilter, deptFilter);
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to reject request. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setProcessing(false);
    }
  }, [selectedRequest, rejectionReason, statusFilter, deptFilter, fetchRequests]);

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h5" fontWeight="bold">
            Student Portal Requests
          </MDTypography>
          <Box display="flex" gap={1} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchRequests(statusFilter, deptFilter)} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2} sx={{ mt: 2 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Autocomplete
              size="small"
              sx={{ minWidth: 180 }}
              options={['Pending', 'Approved', 'Rejected', 'All']}
              value={statusFilter === null ? 'All' : statusFilter}
              onChange={(event, newValue) => {
                setStatusFilter(newValue === 'All' ? null : newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Filter by Status" />
              )}
            />
            <MDTypography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              Total Requests: <strong>{rowData.length}</strong>
            </MDTypography>
          </Box>

          <div className="ag-theme-alpine" style={{ height: '60vh', width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              animateRows={true}
              rowHeight={35}
              headerHeight={32}
              pagination={true}
              paginationPageSize={20}
              suppressSizeToFit={true}
            />
          </div>
          <style>{`
            .ag-theme-alpine .ag-header-cell-label {
              font-size: 11px;
              font-weight: 600;
              padding: 4px 8px;
            }
            .ag-theme-alpine .ag-cell {
              font-size: 11px;
              padding: 4px 8px;
              line-height: 1.4;
            }
            .ag-theme-alpine .ag-row {
              min-height: 35px;
            }
            .ag-theme-alpine .ag-header-row {
              min-height: 32px;
            }
            .ag-theme-alpine .ag-header-cell {
              padding: 0 4px;
            }
            .ag-theme-alpine .ag-cell-wrapper {
              padding: 0;
            }
          `}</style>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">
          Close
        </MDButton>
      </DialogActions>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => {
        setApproveDialogOpen(false);
        setRemarks('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6">Approve Request</MDTypography>
            <IconButton size="small" onClick={() => {
              setApproveDialogOpen(false);
              setRemarks('');
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to approve the request for <strong>{selectedRequest?.studentName}</strong>?
            This will automatically connect the student to the portal.
          </MDTypography>
          <TextField
            fullWidth
            label="Remarks (Optional)"
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add any remarks or notes..."
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => {
            setApproveDialogOpen(false);
            setRemarks('');
          }} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleApprove} color="success" variant="contained" disabled={processing}>
            {processing ? 'Processing...' : 'Approve'}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => {
        setRejectDialogOpen(false);
        setRejectionReason('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6">Reject Request</MDTypography>
            <IconButton size="small" onClick={() => {
              setRejectDialogOpen(false);
              setRejectionReason('');
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" sx={{ mb: 2 }}>
            Rejecting request for <strong>{selectedRequest?.studentName}</strong>
          </MDTypography>
          <TextField
            fullWidth
            label="Rejection Reason *"
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
            error={!rejectionReason.trim()}
            helperText={!rejectionReason.trim() ? 'Rejection reason is required' : ''}
            placeholder="Please provide a reason for rejection..."
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => {
            setRejectDialogOpen(false);
            setRejectionReason('');
          }} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleReject} color="error" variant="contained" disabled={processing || !rejectionReason.trim()}>
            {processing ? 'Processing...' : 'Reject'}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Request Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6">Request Details</MDTypography>
            <IconButton size="small" onClick={() => setDetailsDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text.secondary">Student Name</MDTypography>
                  <MDTypography variant="body1" fontWeight="medium">{selectedRequest.studentName}</MDTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text.secondary">ERP Student ID</MDTypography>
                  <MDTypography variant="body1" fontWeight="medium">{selectedRequest.erpStudentId}</MDTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text.secondary">Roll Number</MDTypography>
                  <MDTypography variant="body1">{selectedRequest.rollNum || 'N/A'}</MDTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text.secondary">Registration Number</MDTypography>
                  <MDTypography variant="body1">{selectedRequest.regNum || 'N/A'}</MDTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text.secondary">Email</MDTypography>
                  <MDTypography variant="body1">{selectedRequest.email || 'N/A'}</MDTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text.secondary">Mobile</MDTypography>
                  <MDTypography variant="body1">{selectedRequest.mobile || 'N/A'}</MDTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text.secondary">WhatsApp</MDTypography>
                  <MDTypography variant="body1">{selectedRequest.whatsAppNumber || 'N/A'}</MDTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text.secondary">Batch Year</MDTypography>
                  <MDTypography variant="body1">{selectedRequest.batchYear || 'N/A'}</MDTypography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text.secondary">Requested Date</MDTypography>
                  <MDTypography variant="body1">
                    {selectedRequest.requestedDate ? new Date(selectedRequest.requestedDate).toLocaleString() : 'N/A'}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text.secondary">Status</MDTypography>
                  <Box>
                    <Chip 
                      label={selectedRequest.status} 
                      color={selectedRequest.status === 'Approved' ? 'success' : selectedRequest.status === 'Rejected' ? 'error' : 'warning'}
                      size="small"
                    />
                  </Box>
                </Grid>
                {selectedRequest.remarks && (
                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text.secondary">Remarks</MDTypography>
                    <MDTypography variant="body2" sx={{ mt: 0.5, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedRequest.remarks}
                    </MDTypography>
                  </Grid>
                )}
                {selectedRequest.rejectionReason && (
                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text.secondary">Rejection Reason</MDTypography>
                    <MDTypography variant="body2" sx={{ mt: 0.5, p: 1, bgcolor: 'error.lighter', borderRadius: 1, color: 'error.main' }}>
                      {selectedRequest.rejectionReason}
                    </MDTypography>
                  </Grid>
                )}
                {selectedRequest.approvedDate && (
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text.secondary">Approved Date</MDTypography>
                    <MDTypography variant="body1">
                      {new Date(selectedRequest.approvedDate).toLocaleString()}
                    </MDTypography>
                  </Grid>
                )}
                {selectedRequest.rejectedDate && (
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text.secondary">Rejected Date</MDTypography>
                    <MDTypography variant="body1">
                      {new Date(selectedRequest.rejectedDate).toLocaleString()}
                    </MDTypography>
                  </Grid>
                )}
                {selectedRequest.isConnected && selectedRequest.connectedDate && (
                  <Grid item xs={12}>
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                      Student connected to portal on {new Date(selectedRequest.connectedDate).toLocaleDateString()}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDetailsDialogOpen(false)} color="secondary">
            Close
          </MDButton>
          {selectedRequest?.status === 'Pending' && (
            <>
              <MDButton 
                onClick={() => {
                  setDetailsDialogOpen(false);
                  setApproveDialogOpen(true);
                }} 
                color="success"
                variant="contained"
              >
                Approve
              </MDButton>
              <MDButton 
                onClick={() => {
                  setDetailsDialogOpen(false);
                  setRejectDialogOpen(true);
                }} 
                color="error"
                variant="contained"
              >
                Reject
              </MDButton>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

AdminStudentRequestsManager.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminStudentRequestsManager;

